import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

// ─── Constants ───
const THEME_STORAGE_KEY = 'hrv4-theme-mode';
const VALID_MODES = ['light', 'dark', 'system'];

// ─── Context ───
const ThemeContext = createContext({
  themeMode: 'system',     // 'light' | 'dark' | 'system'
  resolvedTheme: 'light',  // actual applied theme: 'light' | 'dark'
  isDark: false,
  setThemeMode: () => {},
});

// ─── Provider ───

export function ThemeProvider({ children }) {
  // Read initial mode from localStorage (or default to 'system')
  const getInitialMode = () => {
    try {
      const stored = localStorage.getItem(THEME_STORAGE_KEY);
      if (stored && VALID_MODES.includes(stored)) return stored;
    } catch {}
    return 'system';
  };

  const [themeMode, setThemeModeState] = useState(getInitialMode);
  const [systemPrefersDark, setSystemPrefersDark] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  // Listen to OS-level color scheme changes
  useEffect(() => {
    if (!window.matchMedia) return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e) => setSystemPrefersDark(e.matches);
    if (mq.addEventListener) mq.addEventListener('change', handler);
    else mq.addListener(handler); // older Safari
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', handler);
      else mq.removeListener(handler);
    };
  }, []);

  // Resolve actual theme
  const resolvedTheme =
    themeMode === 'system' ? (systemPrefersDark ? 'dark' : 'light') : themeMode;
  const isDark = resolvedTheme === 'dark';

  // Apply data-theme attribute to <html>
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', resolvedTheme);
  }, [resolvedTheme]);

  // Persist mode + setter
  const setThemeMode = useCallback((mode) => {
    if (!VALID_MODES.includes(mode)) return;
    setThemeModeState(mode);
    try { localStorage.setItem(THEME_STORAGE_KEY, mode); } catch {}
  }, []);

  return (
    <ThemeContext.Provider value={{ themeMode, resolvedTheme, isDark, setThemeMode }}>
      {children}
    </ThemeContext.Provider>
  );
}

// ─── Hook ───
export function useTheme() {
  return useContext(ThemeContext);
}